import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const KAKAO_REST_API_KEY = '7b084dce651d696a9b15e34dc7d6f5b5';
const KAKAO_REDIRECT_URI = window.location.hostname === 'localhost'
  ? 'http://localhost:3000/auth/kakao/callback'
  : 'https://campuslife-assistant.com/auth/kakao/callback';

const universityDomains = {
  '한남대학교': 'hannam.ac.kr',
  '한밭대학교': 'hanbat.ac.kr',
  '충남대학교': 'cnu.ac.kr'
};

function RegisterPage() {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL || 'https://campus-life-production.up.railway.app';

  const [kakaoVerified, setKakaoVerified] = useState(false);
  const [kakaoName, setKakaoName] = useState('');
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [formData, setFormData] = useState({
    studentId: '',
    name: '',
    university: '',
    email: '',
    password: '',
    confirmPassword: '',
    kakaoId: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [kakaoDuplicate, setKakaoDuplicate] = useState(false);

  useEffect(() => {
    const duplicate = sessionStorage.getItem('kakaoDuplicate');
    if (duplicate === 'true') {
      sessionStorage.removeItem('kakaoDuplicate');
      setKakaoDuplicate(true);
      return;
    }

    const verified = sessionStorage.getItem('kakaoVerified');
    const name = sessionStorage.getItem('kakaoName');
    const kakaoId = sessionStorage.getItem('kakaoId') || '';
    if (verified === 'true') {
      setKakaoVerified(true);
      setKakaoName(name || '');
      setFormData(prev => ({ ...prev, name: name || '', kakaoId }));
      sessionStorage.removeItem('kakaoVerified');
      sessionStorage.removeItem('kakaoName');
      sessionStorage.removeItem('kakaoId');
    }
  }, []);

  const showAlert = (msg, isSuccess = false) => {
    setAlertMessage(msg);
    setIsSuccessAlert(isSuccess);
    setShowAlertModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUniversityChange = (e) => {
    const university = e.target.value;
    const domain = universityDomains[university] || '';
    const studentId = formData.studentId;
    setFormData(prev => ({
      ...prev,
      university,
      email: studentId && domain ? `${studentId}@${domain}` : prev.email
    }));
  };

  const handleStudentIdChange = (e) => {
    const studentId = e.target.value;
    const domain = universityDomains[formData.university] || '';
    setFormData(prev => ({
      ...prev,
      studentId,
      email: studentId && domain ? `${studentId}@${domain}` : prev.email
    }));
  };

  const handleKakaoAuth = () => {
    const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${KAKAO_REST_API_KEY}&redirect_uri=${KAKAO_REDIRECT_URI}&response_type=code&prompt=login`;
    window.location.href = kakaoAuthUrl;
  };

  const handleRegister = async () => {
    const { studentId, name, university, email, password, confirmPassword } = formData;

    if (!kakaoVerified) { showAlert('카카오 본인인증을 먼저 완료해주세요.'); return; }
    if (!studentId || !name || !university || !email) { showAlert('모든 정보를 입력해주세요.'); return; }
    if (!email.includes('@') || !email.includes('.ac.kr')) { showAlert('올바른 학교 이메일을 입력해주세요.'); return; }
    const expectedDomain = universityDomains[university];
    if (expectedDomain && !email.endsWith(expectedDomain)) { showAlert(`${university} 이메일(@${expectedDomain})을 사용해주세요.`); return; }
    if (password.length < 8) { showAlert('비밀번호는 최소 8글자 이상이어야 합니다.'); return; }
    if (!/[a-zA-Z]/.test(password)) { showAlert('비밀번호에 영문자를 최소 1개 포함해주세요.'); return; }
    if (!/[0-9]/.test(password)) { showAlert('비밀번호에 숫자를 최소 1개 포함해주세요.'); return; }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) { showAlert('비밀번호에 특수문자를 최소 1개 포함해주세요.'); return; }
    if (password !== confirmPassword) { showAlert('비밀번호가 일치하지 않습니다.'); return; }
    if (!privacyAgreed) { showAlert('개인정보처리방침에 동의해주세요.'); return; }

    setIsLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, university, studentId, kakaoId: formData.kakaoId })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showAlert('회원가입이 완료되었습니다! 로그인 해주세요.', true);
      } else {
        showAlert(data.message || '회원가입에 실패했습니다.');
      }
    } catch {
      showAlert('서버 연결에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <button onClick={() => navigate('/')} style={backBtn}>← 이전으로</button>
      <div style={formWrapper}>
        <h2 style={titleStyle}>회원가입</h2>
        <p style={descStyle}>카카오 본인인증 후 학교 정보를 입력해주세요.</p>

        {kakaoDuplicate ? (
          <div style={duplicateBadge}>
            ⚠️ 이미 가입된 카카오 계정입니다.<br />
            <span onClick={() => navigate('/')} style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}>
              로그인 하러 가기
            </span>
          </div>
        ) : !kakaoVerified ? (
          <button style={kakaoBtn} onClick={handleKakaoAuth}>
            <img src="https://developers.kakao.com/assets/img/about/logos/kakaolink/kakaolink_btn_medium.png"
              alt="kakao" style={{ width: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
            카카오로 본인인증
          </button>
        ) : (
          <div style={verifiedBadge}>
            ✅ 카카오 인증 완료 {kakaoName && `(${kakaoName})`}
          </div>
        )}

        {kakaoVerified && (
          <div style={inputGroup}>
            <select name="university" style={inputStyle} onChange={handleUniversityChange} value={formData.university}>
              <option value="">학교를 선택해주세요</option>
              <option value="한남대학교">한남대학교</option>
              <option value="한밭대학교">한밭대학교</option>
              <option value="충남대학교">충남대학교</option>
            </select>
            <input name="studentId" type="text" placeholder="학번" style={inputStyle} onChange={handleStudentIdChange} value={formData.studentId} />
            <input name="name" type="text" placeholder="이름" style={inputStyle} onChange={handleChange} value={formData.name} />
            <input name="email" type="text" placeholder="학교 이메일 (예: 학번@학교.ac.kr)" style={inputStyle} onChange={handleChange} value={formData.email} />
            <input name="password" type="password" placeholder="비밀번호 (8자 이상, 영문/숫자/특수문자 포함)" style={inputStyle} onChange={handleChange} value={formData.password} />
            <input name="confirmPassword" type="password" placeholder="비밀번호 확인" style={inputStyle} onChange={handleChange} value={formData.confirmPassword} />

            {/* 개인정보처리방침 동의 */}
            <div style={privacyRowStyle}>
              <input type="checkbox" id="privacy" checked={privacyAgreed}
                onChange={() => { if (!privacyAgreed) setShowPrivacyModal(true); else setPrivacyAgreed(false); }}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
              <label htmlFor="privacy" style={{ fontSize: '14px', color: '#444', cursor: 'pointer' }}
                onClick={() => { if (!privacyAgreed) setShowPrivacyModal(true); else setPrivacyAgreed(false); }}>
                (필수){' '}
                <span style={{ color: '#4A90E2', textDecoration: 'underline' }}>
                  개인정보처리방침
                </span>
                에 동의합니다.
              </label>
            </div>

            <button style={mainBtn} onClick={handleRegister} disabled={isLoading}>
              {isLoading ? '처리 중...' : '가입하기'}
            </button>
          </div>
        )}

        <div style={linkGroup}>
          <span style={{ color: '#999', fontSize: '14px' }}>이미 계정이 있으신가요? </span>
          <span onClick={() => navigate('/')} style={{ color: '#000', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}>로그인</span>
        </div>
      </div>

      {/* 개인정보처리방침 모달 */}
      {showPrivacyModal && (
        <div style={customModalOverlayStyle}>
          <div style={{ ...customModalContentStyle, maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto', textAlign: 'left' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', textAlign: 'center' }}>개인정보처리방침</h3>
            <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.9' }}>
              <p style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>개인정보처리방침</p>
              <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '12px' }}>Campus Life Assistant(이하 "서비스")는 개인정보보호법 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 다음과 같이 개인정보처리방침을 수립·공개합니다.</p>

              <p><strong>제1조 (개인정보의 처리목적)</strong></p>
              <p>서비스는 다음의 목적을 위하여 개인정보를 처리합니다.</p>
              <p>① 회원 가입 및 관리: 회원제 서비스 이용에 따른 본인 확인, 개인 식별, 가입 의사 확인</p>
              <p>② 서비스 제공: AI 강의 학습, AI 플래너, 팀플 관리, 공지사항 등 학교생활 지원 서비스 제공</p>
              <p>③ 민원처리: 민원인의 신원 확인, 민원사항 확인 및 처리결과 통보</p>
              <br />

              <p><strong>제2조 (처리하는 개인정보 항목)</strong></p>
              <p>① 필수항목: 이름, 학번, 학교 이메일 주소, 소속 대학교, 비밀번호, 카카오 계정 식별자(ID)</p>
              <p>② 서비스 이용 과정에서 자동 생성되는 정보: 서비스 이용 기록, 접속 로그, 쿠키</p>
              <br />

              <p><strong>제3조 (개인정보의 처리 및 보유기간)</strong></p>
              <p>① 서비스는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
              <p>② 회원 탈퇴 시 즉시 파기합니다. 단, 관련 법령에 따라 보존할 필요가 있는 경우 해당 기간 동안 보관합니다.</p>
              <p>- 소비자의 불만 또는 분쟁처리에 관한 기록: 3년 (전자상거래법)</p>
              <p>- 로그인 기록: 3개월 (통신비밀보호법)</p>
              <br />

              <p><strong>제4조 (개인정보의 제3자 제공)</strong></p>
              <p>① 서비스는 정보주체의 개인정보를 제1조에서 명시한 목적 범위 내에서만 처리하며, 정보주체의 동의 없이 제3자에게 제공하지 않습니다.</p>
              <p>② 다음의 경우에는 예외로 합니다.</p>
              <p>- 정보주체가 사전에 동의한 경우</p>
              <p>- 법령의 규정에 의거하거나 수사 목적으로 법령에 정해진 절차에 따라 요청한 경우</p>
              <br />

              <p><strong>제5조 (개인정보처리 위탁)</strong></p>
              <p>서비스는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁합니다.</p>
              <p>① 위탁받는 자: 카카오(주)</p>
              <p>② 위탁하는 업무의 내용: 카카오 소셜 로그인을 통한 본인 인증 서비스 제공</p>
              <p>③ 위탁 기간: 회원 탈퇴 시 또는 위탁 계약 종료 시까지</p>
              <br />

              <p><strong>제6조 (정보주체의 권리·의무 및 행사방법)</strong></p>
              <p>① 정보주체는 서비스에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다.</p>
              <p>- 개인정보 열람 요구</p>
              <p>- 오류 등이 있을 경우 정정 요구</p>
              <p>- 삭제 요구</p>
              <p>- 처리정지 요구</p>
              <p>② 권리 행사는 서비스를 통하여 하실 수 있으며, 서비스는 이에 대해 지체 없이 조치하겠습니다.</p>
              <br />

              <p><strong>제7조 (개인정보의 파기)</strong></p>
              <p>① 서비스는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.</p>
              <p>② 파기절차: 불필요한 개인정보는 개인정보 보호책임자의 승인을 받아 파기합니다.</p>
              <p>③ 파기방법: 전자적 파일 형태로 기록·저장된 개인정보는 기록을 재생할 수 없도록 파기합니다.</p>
              <br />

              <p><strong>제8조 (개인정보 보호책임자)</strong></p>
              <p>① 서비스는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 정보주체의 개인정보 관련 불만처리 및 피해구제를 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.</p>
              <p>- 개인정보 보호책임자: Campus Life Assistant 운영팀</p>
              <p>- 연락처: 서비스 내 문의하기</p>
              <br />

              <p><strong>제9조 (개인정보처리방침의 변경)</strong></p>
              <p>이 개인정보처리방침은 2026년 5월 22일부터 적용됩니다. 법령 및 방침에 따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
              <button style={{ padding: '10px 30px', background: '#000', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                onClick={() => { setPrivacyAgreed(true); setShowPrivacyModal(false); }}>
                동의하기
              </button>
              <button style={{ padding: '10px 30px', background: '#f3f4f6', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                onClick={() => setShowPrivacyModal(false)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {showAlertModal && (
        <div style={customModalOverlayStyle}>
          <div style={customModalContentStyle}>
            <h3 style={customModalTitleStyle}>{alertMessage}</h3>
            <button style={{ ...customModalConfirmButtonStyle, backgroundColor: '#000', padding: '10px 40px' }}
              onClick={() => { setShowAlertModal(false); if (isSuccessAlert) navigate('/'); }}>
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const containerStyle = { maxWidth: '450px', margin: '0 auto', padding: '100px 20px', position: 'relative', fontFamily: 'Pretendard, sans-serif' };
const backBtn = { position: 'absolute', top: '40px', left: '20px', padding: '8px 16px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', cursor: 'pointer', fontWeight: '600' };
const formWrapper = { textAlign: 'center' };
const titleStyle = { fontSize: '28px', fontWeight: '800', marginBottom: '10px' };
const descStyle = { color: '#666', marginBottom: '30px', fontSize: '15px' };
const kakaoBtn = { width: '100%', padding: '16px', background: '#FEE500', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '20px' };
const verifiedBadge = { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '14px', padding: '14px', color: '#16a34a', fontWeight: '600', marginBottom: '20px' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' };
const inputStyle = { padding: '16px', borderRadius: '14px', border: '1px solid #E5E7EB', fontSize: '16px', outline: 'none', background: '#F9FAFB', width: '100%', boxSizing: 'border-box' };
const privacyRowStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' };
const mainBtn = { width: '100%', padding: '18px', background: '#000', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' };
const linkGroup = { marginTop: '20px' };
const customModalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 };
const customModalContentStyle = { backgroundColor: '#fff', padding: '30px 40px', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', textAlign: 'center', minWidth: '320px' };
const customModalTitleStyle = { fontSize: '18px', fontWeight: '600', color: '#333', margin: '0 0 25px 0', lineHeight: '1.5' };
const customModalConfirmButtonStyle = { border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#fff' };
const duplicateBadge = { background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '14px', padding: '14px', color: '#ea580c', fontWeight: '600', marginBottom: '20px', lineHeight: '2' };

export default RegisterPage;